/**
 * How a place is written out for a reader.
 *
 * Its own module, with no imports, for two reasons. It is pure - given a row it returns a string -
 * and `lookups.ts` around it reaches the network, so a test of this logic would otherwise have to
 * stand up a Supabase client to check a comma. And it has TWO callers: the picker's dropdown rows
 * and the settled chip beside them. They disagreed until now, which is exactly how "West Bengal,
 * West Bengal" survived: the fix went into one of them.
 */

export interface GeographyHit {
  id: string;
  name: string;
  local_name: string | null;
  /**
   * Which level this row is. Optional because every caller before the office form was a village
   * and some still do not ask for it; absent is treated as a village.
   */
  geography_type?: string | null;
  block_name: string | null;
  district_name: string | null;
  state_name: string | null;
}

/**
 * Just the places ABOVE this one, comma separated.
 *
 * Every row carries its own block, district and state names - and a STATE's state_name is itself -
 * so appending all three blindly printed a state twice the moment the picker was asked for
 * anything but a village. A parent that repeats the place is dropped as well, which covers a
 * district and its headquarters sharing a name.
 */
export function geographyParents(hit: GeographyHit): string {
  const above = ((): Array<string | null> => {
    switch (hit.geography_type) {
      case 'country':
      case 'state': return [];
      case 'district': return [hit.state_name];
      case 'block': return [hit.district_name, hit.state_name];
      default: return [hit.block_name, hit.district_name, hit.state_name];
    }
  })();
  return above.filter((part): part is string => Boolean(part) && part !== hit.name).join(', ');
}

/** "Village (local name), Block, District, State" - what the reader recognises, in their order. */
export function geographyPath(hit: GeographyHit): string {
  const local = hit.local_name && hit.local_name !== hit.name ? ` (${hit.local_name})` : '';
  return [`${hit.name}${local}`, geographyParents(hit)].filter(Boolean).join(', ');
}
